<script setup lang="ts">
/**
 * Aventross — AGM (Audited Companies) (CAV001). Studio panel: 원본 합본 1파일 + create.
 *   cdd/CddVarField/useCddFill/AgmSigners 는 aura/agm 재사용(Singapura 와 같은 방식).
 *   사람·표 행 반복은 useRepeat 목록(mem/dir/ctrl/nctrl/nd/ns, 이사별 이해관계 dir_{i}/int) — 값은 전부 fill.
 *   서명 [[Signers]] = Directors' Resolutions 의 이사 서명(agm_Signers). 나머지 서명란은 원본 고정 선 + 이름 변수.
 *   자동채움: 회사·등록번호·등록주소(=AGM 장소 기본)·FYE + 주주 + 이사 + 감사인(엔티티).
 *     Annex A·B·C 는 레지스터 등록부(Register of Controllers / Nominee Directors /
 *     Nominee Shareholders)에서만 — 등록부에 없으면 채우지 않는다.
 *     컨트롤러 Notice(s386AG)는 엔티티 현재 컨트롤러(nctrl) — 개인 (a) · 법인 (b).
 */
import { ref, computed, watch, onMounted, provide } from 'vue';
import { useRouter } from 'vue-router';
import type { PreSetSelection } from '@/features/startTask/preset_task/shared/preset/types/preset.types';
import type { OfficerDetail, SignerEntry } from '@/features/AI-documents/types/docGen.types';
import { usePresetProgress } from '@/features/startTask/preset_task/shared/preset/composables/usePresetProgress';
import { provideAgmFill, bareKey, scopeOf, typeOf } from '../../../aura/agm/cdd/useCddFill';
import { provideAgmSigners, AGM_ENTITY_CD } from '../../../aura/agm/cdd/agmSignersContext';
import { sgAppliedAddressLine } from '@/features/startTask/preset_task/sg/preset/utils/sgAppliedAddressLine';
import { getTaskConfigByCode } from '@/features/startTask/api/taskConfigApi';
import { entityMyApi } from '@/features/Entity_my/api/entityApi';
import { isIndividualNominator, formatNominatorAddress, formatNominatorPhone } from '@/features/Entity/utils/nominatorFormat';
import { fetchRegisterEntries } from '@/features/register/api/registerApi';
import { baseCodeApi } from '@/shared/api/baseCodeApi';
import AventrossAgmPackageDoc from '../AventrossAgmPackageDoc.vue';
import { normalizeAventrossAgmForWord } from '../utils/normalizeAventrossAgmForWord';
import { countKey, repeatCount, setRepeatCount } from '../useRepeat';

const props = defineProps<{
  selection: PreSetSelection;
  entityData: Record<string, any> | null;
  taskTitle: string;
  isReady: boolean;
  currentStep: string;
  selectedPerson?: OfficerDetail | null;
  selectedAddress?: { value: string; type: string; position?: string } | null;
  appliedAddress?: {
    key: string; line?: string; line1?: string; line2?: string; line3?: string;
    town?: string; state?: string; postcode?: string; country?: string; seq?: number;
  } | null;
  editTaskId?: number | null;
  studio?: boolean;
  chatFormData?: Record<string, any> | null;
}>();

const emit = defineEmits<{
  (e: 'back'): void;
  (e: 'task-created', data: { taskId: number; taskTitle: string }): void;
  (e: 'request-person-lookup', ctx: Record<string, any>): void;
  (e: 'change-entity'): void;
  (e: 'change-task-title'): void;
  (e: 'progress-change', pct: number): void;
  (e: 'request-address-input', key: string): void;
}>();

const TASK_CODE = 'CAV001';
const DOC_TYPE = 'SG_AVENTROSS_AGM_AUDITED';
/** 화면 반복 목록 base — 회사가 바뀌면 통째로 비운다 */
const LIST_BASES = ['mem', 'dir', 'ctrl', 'nctrl', 'nd', 'ns'];

const router = useRouter();
const fill = provideAgmFill();
const signersCtx = provideAgmSigners();
const autofilledKeys = ref<Set<string>>(new Set());
const lastAutofillCd = ref('');
const taskConfigSteps = ref<string[]>([]);
/** Edit 재진입 — 저장값을 복원했으면 회사 자동채움이 직접 고친 값·목록을 덮지 않는다(빈 칸만 채움). */
const restoredFromSaved = ref(false);

const st = (v: unknown) => String(v ?? '').trim();
/**
 * 날짜 → ISO(YYYY-MM-DD). 등록부 값은 출처마다 형식이 다르다:
 *   직접 입력 = YYYY-MM-DD / dd/MM/yyyy, BizFile 싱크 = "12/29/2023 12:00:00 AM"(.NET 미국식 MM/dd/yyyy + 시각).
 *   ISO 로 맞춰야 화면(세션 형식)·문서(백엔드 변환) 둘 다 바르게 나온다.
 */
function isoDate(v: unknown): string {
  const s = st(v);
  const pad = (n: number) => String(n).padStart(2, '0');
  const iso = s.match(/^(\d{4})-(\d{1,2})-(\d{1,2})/);
  if (iso) return `${iso[1]}-${pad(Number(iso[2]))}-${pad(Number(iso[3]))}`;
  const sl = s.match(/^(\d{1,2})\/(\d{1,2})\/(\d{4})(.*)$/);
  if (sl) {
    const a = Number(sl[1]); const b = Number(sl[2]);
    // 오전/오후 시각이 붙은 .NET 형식은 월이 앞, 그 외(등록부 표기)는 일이 앞. 한쪽이 12 를 넘으면 그쪽이 일.
    let [mo, d] = /[AP]M\s*$/i.test(sl[4]) ? [a, b] : [b, a];
    if (mo > 12 && d <= 12) [mo, d] = [d, mo];
    if (mo >= 1 && mo <= 12 && d >= 1 && d <= 31) return `${sl[3]}-${pad(mo)}-${pad(d)}`;
  }
  return s;
}

/** 공통코드 → 이름: 국적 B04(TOTAL_CODE "B04118" 또는 BIGO1 ISO2 "MY"), 국가 B15(BIGO1 ISO2 "SG").
 *   등록부 목록 API 가 컨트롤러 NATIONALITY_CD 를 이름으로 못 바꿔 오는 경우 + 노미네이터(ISO2 저장)용. */
const codeNames = { b04: new Map<string, string>(), b15: new Map<string, string>() };
async function loadCodeNames() {
  for (const main of ['B04', 'B15'] as const) {
    if (codeNames[main === 'B04' ? 'b04' : 'b15'].size) continue;
    try {
      const res: any = await baseCodeApi.getCodesByMainCode('CM', main, undefined);
      const codes: any[] = res?.codes || res?.data?.codes || [];
      const map = codeNames[main === 'B04' ? 'b04' : 'b15'];
      for (const c of codes) {
        const title = st(c.defaultCodeName || c.DEFAULT_CODE_NAME || c.codeName || c.CODE_NAME);
        if (!title) continue;
        const total = st(c.totalCode || c.TOTAL_CODE).toUpperCase();
        const iso2 = st(c.bigo1 || c.BIGO1).toUpperCase();
        if (total && !map.has(total)) map.set(total, title);
        if (iso2 && !map.has(iso2)) map.set(iso2, title);
      }
    } catch (e) { console.warn(`[Aventross-AGM] ${main} codes failed`, e); }
  }
}
const nationalityName = (v: unknown) => { const s = st(v); return codeNames.b04.get(s.toUpperCase()) || s; };
const countryName = (v: unknown) => { const s = st(v); return codeNames.b15.get(s.toUpperCase()) || s; };
function joinNames(names: string[]): string {
  const n = names.filter(Boolean);
  if (n.length <= 1) return n[0] || '';
  if (n.length === 2) return `${n[0]} and ${n[1]}`;
  return `${n.slice(0, -1).join(', ')} and ${n[n.length - 1]}`;
}
/** 주소 파트 → 한 줄(컨트롤러 상세). 해외주소 우선, 없으면 SG 주소. */
function buildAddress(dt: any): string {
  if (!dt || typeof dt !== 'object') return '';
  const g = (...keys: string[]) => { for (const k of keys) { const v = st(dt[k]); if (v) return v; } return ''; };
  const fl1 = g('foreignAddressLine1', 'FOREIGN_ADDRESS_LINE_1');
  const fl2 = g('foreignAddressLine2', 'FOREIGN_ADDRESS_LINE_2');
  if (fl1 || fl2) return [fl1, fl2].filter(Boolean).join(', ');
  const unit = [g('addrLevel', 'ADDR_LEVEL', 'level'), g('addrUnit', 'ADDR_UNIT', 'unit')].filter(Boolean).join('-');
  const postal = g('postalCode', 'POSTAL_CODE');
  return [
    [g('blockNo', 'BLOCK_NO', 'block'), g('streetName', 'STREET_NAME', 'street')].filter(Boolean).join(' '),
    g('buildingName', 'BUILDING_NAME', 'building'), unit ? `#${unit}` : '', postal ? `Singapore ${postal}` : '',
  ].filter(Boolean).join(', ');
}
/** 엔티티 컨트롤러가 법인인가 — categoryCode(B09001 Corporate / B09002 Individual) */
function isCorporateController(c: unknown): boolean {
  const s = st(c);
  return s.toUpperCase() === 'B09001' || /corporate/i.test(s);
}
let signerSeq = 0;
function mkSigner(name: string, title: string, role = 'director'): SignerEntry {
  return { id: `av-agm-${signerSeq++}`, name: st(name), title, role, isManual: true, showTitle: true, _signerLayout: 'box' } as SignerEntry;
}

const entityCd = computed(() =>
  st(props.entityData?.entityCd || props.entityData?.icpCd || props.chatFormData?.entityCd || props.chatFormData?.icpCd),
);
provide(AGM_ENTITY_CD, entityCd); // AgmSigners 의 직책별 서명자 선택 다이얼로그 활성화

const taskTitleLocal = ref('');
const titleModel = computed({ get: () => taskTitleLocal.value || props.taskTitle || '', set: (v: string) => { taskTitleLocal.value = v; } });
const effectiveTitle = computed(() => titleModel.value.trim());
const studio = computed(() => !!props.studio);
const editMode = computed(() => !!props.editTaskId);
const creating = ref(false);
const progress = ref('');
const presetProgress = usePresetProgress();
watch(creating, (v) => { presetProgress.busy = v; presetProgress.message = v ? progress.value || 'Creating task…' : ''; });
watch(progress, (m) => { if (creating.value) presetProgress.message = m; });

function emitProgress() {
  const total = fill.totalFields.value || 1;
  emit('progress-change', Math.min(100, Math.round((fill.filledCount.value / total) * 100)));
}
watch([() => fill.filledCount.value, () => fill.totalFields.value], () => emitProgress());

// ── ✨ 사람 조회 · 📍 주소 ─────────────────────────────────
function lookupRoleFor(key: string): string {
  const b = bareKey(key);
  const sc = scopeOf(key);
  if (/^n?ctrl_/.test(sc) || /^controller_/.test(b)) return 'CONTROLLER';
  if (/^ns_/.test(sc) || b === 'member_name' || b === 'corporate_member_name') return 'SHAREHOLDER';
  if (/^nd_/.test(sc) || /director|chairman/.test(b)) return 'DIRECTOR';
  return 'CONTACT';
}
fill.setActivateHandler((key: string) => {
  const t = typeOf(key);
  if (t === 'entity') { emit('change-entity'); return; }
  if (t === 'person') {
    emit('request-person-lookup', { fieldGroup: 'aventross-agm', key: bareKey(key), scopedKey: key, defaultRole: lookupRoleFor(key), lookupType: 'officer', entityCd: entityCd.value });
  }
});
fill.setAddressHandler((key: string) => { emit('request-address-input', key); });

function personField(p: any, ...keys: string[]): string {
  for (const k of keys) { const v = st(p?.[k]); if (v) return v; }
  return '';
}
const CONTROLLER_PERSON_KEYS = [
  'controller_full_name', 'controller_alias', 'controller_residential_address', 'controller_email', 'controller_contact_no',
  'controller_nationality', 'controller_id_no', 'controller_date_of_birth', 'controller_start_date',
];
const CONTROLLER_CORP_KEYS = [
  'controller_corp_name', 'controller_corp_uen', 'controller_corp_registered_address', 'controller_corp_email',
  'controller_corp_contact_no', 'controller_corp_legal_form', 'controller_corp_jurisdiction_statute',
  'controller_corp_entity_register', 'controller_corp_register_no', 'controller_corp_start_date',
];
/** ✨ 컨트롤러 이름 칸에서 고른 사람 → 그 컨트롤러 항목의 인적사항(Notice·Annex A 공용) */
function applyControllerPerson(sc: string, p: any) {
  // 다른 사람으로 바꿀 때 이전 사람 값이 섞이지 않게 인적사항 칸을 먼저 비운다
  //   (Annex A 의 Entry·확인·중지일, Notice Reply date 같은 기록 날짜는 유지)
  for (const k of [...CONTROLLER_PERSON_KEYS, ...CONTROLLER_CORP_KEYS]) fill.setValue(sc + k, '');
  const set = (k: string, v: string) => { if (st(v)) fill.setValue(sc + k, st(v)); };
  const name = personField(p, 'name', 'fullName', 'contactName', 'CONTACT_NAME');
  // Notice 에서 법인을 고르면 1번 (b) 법인 칸
  if (/^nctrl_/.test(sc) && isCorporateController(personField(p, 'categoryCd', 'categoryCode', 'CATEGORY_CD'))) {
    set('controller_name', name);
    set('controller_corp_name', name);
    set('controller_corp_uen', personField(p, 'contactRegNo', 'uen', 'CONTACT_REG_NO'));
    set('controller_corp_registered_address', buildAddress(p) || personField(p, 'primaryAddress', 'address'));
    set('controller_corp_email', personField(p, 'contactEmail', 'email', 'EMAIL'));
    set('controller_corp_contact_no', [personField(p, 'areaCode'), personField(p, 'mobileNo', 'contactNo', 'MOBILE_NO')].filter(Boolean).join(' '));
    set('controller_corp_start_date', isoDate(personField(p, 'dateOfAppointed', 'dateOfBecoming', 'becomingDate')));
    return;
  }
  set('controller_name', name);
  if (/^nctrl_/.test(sc)) set('controller_full_name', name); // Notice 1번 (a)(i)
  set('controller_alias', personField(p, 'aliases', 'alias', 'aka'));
  set('controller_residential_address', buildAddress(p) || personField(p, 'residentialAddress', 'address', 'primaryAddress'));
  set('controller_email', personField(p, 'email', 'contactEmail', 'EMAIL'));
  set('controller_contact_no', personField(p, 'contactNo', 'mobileNo', 'MOBILE_NO'));
  set('controller_nationality', personField(p, 'nationalityName', 'nationality', 'NATIONALITY_NAME'));
  set('controller_id_no', personField(p, 'identificationNo', 'nricNo', 'idNo', 'contactRegNo', 'IDENTIFICATION_NO'));
  set('controller_date_of_birth', isoDate(personField(p, 'dateOfBirth', 'dob')));
  set('controller_start_date', isoDate(personField(p, 'dateOfAppointed', 'dateOfBecoming', 'becomingDate')));
}
watch(() => props.selectedPerson, (p) => {
  if (!p || !fill.activeKey.value) return;
  const key = fill.activeKey.value;
  if (/^n?ctrl_\d+\/$/.test(scopeOf(key)) && bareKey(key) === 'controller_name') applyControllerPerson(scopeOf(key), p);
  else fill.applyPersonToActive(p as Record<string, any>);
  emitProgress();
});
watch(() => props.selectedAddress, (a) => {
  if (!a?.value || !fill.activeKey.value) return;
  fill.setValue(fill.activeKey.value, a.value); emitProgress();
});
watch(() => props.appliedAddress, (a) => {
  if (!a?.key) return;
  const line = sgAppliedAddressLine(a);
  if (line) { fill.setValue(a.key, line); emitProgress(); }
}, { deep: true });

// ── 자동채움 ─────────────────────────────────────────────
async function autofillFromEntity(cdRaw: string) {
  const cd = st(cdRaw);
  if (!cd || cd === lastAutofillCd.value) return;
  const entityChanged = !!lastAutofillCd.value && lastAutofillCd.value !== cd;
  if (entityChanged) restoredFromSaved.value = false; // 다른 회사로 바꾸면 저장값 보존이 아니라 새 회사 값
  lastAutofillCd.value = cd;
  if (!restoredFromSaved.value) {
    for (const k of autofilledKeys.value) fill.setValue(k, '');
    // 이전 회사의 목록(사람·이해관계 행)과 개수 — 직접 고친 값 포함 — 을 비운다
    for (const k of Object.keys(fill.values)) {
      if (LIST_BASES.some((b) => k.startsWith(`${b}_`) || k === countKey(b))) fill.setValue(k, '');
    }
    signersCtx.clearAll();
  }
  const written = new Set<string>();
  const set = (k: string, v: unknown) => {
    const s = st(v);
    if (!s) return;
    if (restoredFromSaved.value && st(fill.values[k])) return; // 저장값 보존
    fill.setValue(k, s); written.add(k);
  };
  /** 목록 시드 — 저장값을 복원한 목록(개수 있음)은 그대로 둔다 */
  const seedList = (base: string, n: number): boolean => {
    if (restoredFromSaved.value && repeatCount(fill.values, base) > 0) return false;
    setRepeatCount(fill, base, n);
    return true;
  };

  // 1) 회사 · 주주
  let members: any[] = [];
  try {
    const res: any = await fetch(`/api/entity/${encodeURIComponent(cd)}/agm-source`, { credentials: 'include' }).then((r) => r.json());
    const src: any = res?.data?.data ?? res?.data ?? res ?? {};
    set('company_name', src.companyName);
    set('company_registration_no', src.registrationNo);
    set('financial_year_end', isoDate(src.financialYearEnd));
    if (src.registeredOfficeAddress) { set('registered_office_address', src.registeredOfficeAddress); set('agm_venue', src.registeredOfficeAddress); }
    members = Array.isArray(src.members) ? src.members.filter((m: any) => st(m?.name)) : [];
  } catch (e) { console.warn('[Aventross-AGM] agm-source failed', e); }
  const memNames = [...new Set(members.map((m: any) => st(m.name)))];
  if (memNames.length && seedList('mem', memNames.length)) memNames.forEach((nm, i) => set(`mem_${i + 1}/member_name`, nm));

  const officersOf = async (group: 'director' | 'auditor'): Promise<any[]> => {
    try {
      const res: any = await entityMyApi.getEntityOfficer(cd, { positionGroup: group });
      const off: any = res?.data?.data ?? res?.data ?? res ?? {};
      return [...(off.autoOfficers || []), ...(off.manualOfficers || [])].filter((o) => st(o?.cessationDate) === '' && st(o?.status) !== 'Cessation');
    } catch { return []; }
  };

  // 2) 이사 — 출석·Disclosure(이사별)·재선임 명단·의장·서명자(DRW)
  const dirRows: any[] = [];
  const seenDir = new Set<string>();
  for (const o of await officersOf('director')) {
    const nm = st(o.fullName || o.contactName);
    if (!nm || seenDir.has(nm.toUpperCase())) continue;
    seenDir.add(nm.toUpperCase());
    dirRows.push(o);
  }
  const dirNames = dirRows.map((o) => st(o.fullName || o.contactName));
  if (dirNames.length) {
    if (seedList('dir', dirNames.length)) dirNames.forEach((nm, i) => set(`dir_${i + 1}/director_name`, nm));
    set('reelected_directors', joinNames(dirNames));
    set('chairman_name', dirNames[0]);
    set('notice_director_name', dirNames[0]);
    set('minutes_director_name', dirNames[1] || dirNames[0]);
    set('declaring_director_name', dirNames[0]);
    set('declaring_director_id_no', dirRows[0]?.contactRegNo);
    if (!signersCtx.signersOf('agm_Signers').length) signersCtx.setSigners('agm_Signers', dirNames.map((n) => mkSigner(n, 'Director')));
  }

  // 3) 노미니 이사·주주 → Annex B / C
  //    레지스터 등록부(Register of Nominee Directors / Shareholders, JSON_DATA.NOMINATOR)에서만 채운다 —
  //    등록부에 없으면 비워 둔다(엔티티 값으로 대신 채우지 않음).
  /** 노미네이터(bizfile 원본 필드 — 등록부 NOMINATOR) → `${p}_nominator_*` */
  function fillNominatorFields(n: any, disp: any, sc: string, p: string) {
    if (!n || typeof n !== 'object') return;
    const f = (k: string, v: unknown) => set(`${sc}${p}_nominator_${k}`, v);
    const indiv = isIndividualNominator(n);
    f('full_name', indiv ? (n.Name || n.EntityName) : (n.EntityName || n.Name));
    if (indiv) f('alias', n.NameAlias);
    f('residential_address', formatNominatorAddress(n));
    f('email', n.EmailAddress);
    f('contact_no', formatNominatorPhone(n));
    // 개인 = 국적(B04 ISO2), 법인 = 등록국(B15 ISO2) — 코드 대신 이름
    f('nationality', indiv ? nationalityName(disp?.Nationality || n.Nationality) : countryName(disp?.CountryOfRegistration || n.CountryOfRegistration));
    f('id_no', indiv ? n.IdentificationNo : (n.Uen || n.IdentificationNo));
    if (indiv) f('date_of_birth', isoDate(n.BirthDate));
    f('became_date', isoDate(n.NominationDate));
  }
  await loadCodeNames(); // 국적·국가 코드 → 이름(컨트롤러·노미네이터) — 실패해도 진행
  /** 레지스터 등록부 행(JSON_DATA, 공통코드는 이름으로 바뀌어 옴) */
  const registerRows = async (keyTypeName: string): Promise<Array<Record<string, any>>> => {
    try {
      const list = await fetchRegisterEntries({ keyTypeName, entityCd: cd });
      return list.map((r) => r.jsonData || {}).filter((j) => st(j.CONTACT_NAME));
    } catch (e) { console.warn(`[Aventross-AGM] register "${keyTypeName}" failed`, e); return []; }
  };
  const nominatorOf = (j: Record<string, any>): any => {
    const v = j.NOMINATOR;
    if (typeof v === 'string') { try { return JSON.parse(v); } catch { return null; } }
    return v && typeof v === 'object' ? v : null;
  };
  /** 노미니 등록부 행 → Annex B/C 한 벌 */
  function fillNomineeFromRegister(j: Record<string, any>, sc: string, p: string) {
    set(`${sc}${p}_name`, j.CONTACT_NAME);
    fillNominatorFields(nominatorOf(j), {}, sc, p);
    if (!st(fill.values[`${sc}${p}_nominator_became_date`])) set(`${sc}${p}_nominator_became_date`, isoDate(j.APPOINTMENT_DATE));
    set(`${sc}${p}_nominator_ceased_date`, isoDate(j.CESSATION_DATE));
    set(`${sc}${p}_remarks`, j.NOTE);
  }

  const regNd = await registerRows('Register of Nominee Directors');
  if (regNd.length && seedList('nd', regNd.length)) regNd.forEach((j, i) => fillNomineeFromRegister(j, `nd_${i + 1}/`, 'nominee_director'));
  const regNs = await registerRows('Register of Nominee Shareholders');
  if (regNs.length && seedList('ns', regNs.length)) regNs.forEach((j, i) => fillNomineeFromRegister(j, `ns_${i + 1}/`, 'nominee_shareholder'));

  // 4) 감사인
  const aud = (await officersOf('auditor'))[0];
  if (aud) {
    set('auditor_name', st(aud.fullName || aud.contactName || aud.entityName || aud.companyName));
    set('auditor_uen', aud.contactRegNo);
  }

  // 5) 컨트롤러 → Annex A(등록부 전체 — 중지 포함). Notice 는 6) 엔티티 컨트롤러.
  //    레지스터 등록부(Register of Controllers)에서만 채운다 — 등록부에 없으면 비워 둔다(엔티티 값으로 대신 채우지 않음).
  const regCtrl = await registerRows('Register of Controllers');
  if (regCtrl.length) {
    if (seedList('ctrl', regCtrl.length)) {
      regCtrl.forEach((j, i) => {
        const sc = `ctrl_${i + 1}/`;
        set(`${sc}controller_name`, j.CONTACT_NAME);
        set(`${sc}controller_alias`, j.ALIASES);
        set(`${sc}controller_residential_address`, buildAddress(j));
        set(`${sc}controller_email`, j.CONTACT_EMAIL);
        set(`${sc}controller_contact_no`, [j.AREA_CODE, j.MOBILE_NO].map(st).filter(Boolean).join(' '));
        set(`${sc}controller_nationality`, nationalityName(j.NATIONALITY_CD));
        set(`${sc}controller_id_no`, j.CONTACT_REG_NO);
        set(`${sc}controller_date_of_birth`, isoDate(j.DATE_OF_BIRTH));
        set(`${sc}controller_start_date`, isoDate(j.APPOINTMENT_DATE));
        set(`${sc}controller_cessation_date`, isoDate(j.CESSATION_DATE));
        set(`${sc}controller_entry_date`, isoDate(j.DATE_OF_ENTRY));
        set(`${sc}controller_confirmation_received_date`, isoDate(j.DATE_OF_CONFIRMATION));
      });
    }
  }

  // 6) 컨트롤러 → Notice(s386AG) — 엔티티 현재 컨트롤러(L_CONTROLLERMANAGE, `/api/entity/controller/:id`)
  //    Notice 는 현재 컨트롤러에게 보내는 질의서라 등록부(Annex A)가 아니라 엔티티 기준. 종료일 있는 사람 제외.
  //    개인 → 1번 (a) 칸, 법인 → 1번 (b) 칸(고객 요청 2026-11-03: 법인은 (a) 대신 (b)).
  try {
    const res: any = await fetch(`/api/entity/controller/${encodeURIComponent(cd)}`, { credentials: 'include' }).then((r) => r.json());
    let rows: any[] = [];
    for (const c of [res?.data, res?.data?.data, res]) {
      if (Array.isArray(c?.controllers)) { rows = c.controllers; break; }
      if (Array.isArray(c)) { rows = c; break; }
    }
    const seen = new Set<string>();
    const active = rows.filter((c) => {
      const nm = st(c?.name || c?.fullName).toUpperCase();
      if (!nm || seen.has(nm) || /\d/.test(st(c?.dateOfCessation))) return false;
      seen.add(nm);
      return true;
    });
    if (active.length && seedList('nctrl', active.length)) {
      for (let i = 0; i < active.length; i += 1) {
        const c = active[i];
        const sc = `nctrl_${i + 1}/`;
        let dt: any = {};
        const ctrlIdx = c.ctrlIdx ?? c.CTRL_IDX;
        if (ctrlIdx != null && st(ctrlIdx)) {
          try {
            const dres: any = await fetch(
              `/api/entity/controller/${encodeURIComponent(cd)}/detail?ctrlIdx=${encodeURIComponent(st(ctrlIdx))}&registerType=${encodeURIComponent(st(c.registerType) || 'A06001')}`,
              { credentials: 'include' },
            ).then((r) => r.json());
            dt = dres?.data?.data ?? dres?.data ?? {};
          } catch (e) { console.warn('[Aventross-AGM] controller detail failed', e); }
        }
        const name = st(dt.fullName || c.name);
        const contactNo = [dt.areaCode, dt.mobileNo].map(st).filter(Boolean).join(' ');
        const startDate = isoDate(dt.dateOfAppointed || c.dateOfAppointed);
        set(`${sc}controller_name`, name); // 인사말·서명란 이름
        if (isCorporateController(dt.categoryCode || c.categoryCode)) {
          // (ii) Registrar(ACRA) UEN, (vii) 설립지 등록부 번호(ACRA 밖 법인)
          set(`${sc}controller_corp_name`, name);
          set(`${sc}controller_corp_uen`, dt.acrAuen);
          // BizFile 싱크는 싱가포르 법인도 NON_REG_NO 에 UEN 을 같이 넣는다 → 같으면 (vii) 비움
          if (st(dt.nonAcrAuen) !== st(dt.acrAuen)) set(`${sc}controller_corp_register_no`, dt.nonAcrAuen);
          set(`${sc}controller_corp_registered_address`, buildAddress(dt));
          set(`${sc}controller_corp_email`, dt.contactEmail);
          set(`${sc}controller_corp_contact_no`, contactNo);
          set(`${sc}controller_corp_legal_form`, dt.legalForm);
          set(`${sc}controller_corp_jurisdiction_statute`,
            [st(dt.jurisdictionName) || countryName(dt.jurisdiction), st(dt.incorporationStatute)].filter(Boolean).join(', '));
          set(`${sc}controller_corp_entity_register`, dt.corporateEntityRegister);
          set(`${sc}controller_corp_start_date`, startDate);
        } else {
          set(`${sc}controller_full_name`, name); // 1번 (a)(i) — 개인만(법인은 (b)(i))
          set(`${sc}controller_alias`, dt.aliases);
          set(`${sc}controller_residential_address`, buildAddress(dt));
          set(`${sc}controller_email`, dt.contactEmail);
          set(`${sc}controller_contact_no`, contactNo);
          set(`${sc}controller_nationality`, st(dt.nationalityName) || nationalityName(dt.nationalityCd));
          set(`${sc}controller_id_no`, dt.identificationNo);
          set(`${sc}controller_date_of_birth`, isoDate(dt.dateOfBirth));
          set(`${sc}controller_start_date`, startDate);
        }
      }
    }
  } catch (e) { console.warn('[Aventross-AGM] entity controllers failed', e); }

  autofilledKeys.value = written;
  emitProgress();
}
watch(() => props.chatFormData, (fd) => {
  if (!fd) return;
  const nm = st(fd.company_name || fd.entityName);
  if (nm && fill.values.company_name !== nm) fill.setValue('company_name', nm);
  const cd = st(fd.entityCd || fd.icpCd);
  if (cd) void autofillFromEntity(cd);
}, { deep: true });
watch(entityCd, (cd) => { if (cd) void autofillFromEntity(cd); }, { immediate: true });

onMounted(async () => {
  emitProgress();
  try {
    const cfg: any = await getTaskConfigByCode(st(props.selection.task.TASK_CODE) || TASK_CODE);
    taskConfigSteps.value = Array.isArray(cfg?.processSteps) ? cfg.processSteps : [];
  } catch { /* ignore */ }
});

function gatherState() {
  return { values: { ...fill.exportPayload() }, signers: signersCtx.exportAll(), entityCd: entityCd.value, taskTitle: effectiveTitle.value };
}
function restoreState(s: any) {
  if (!s) return;
  restoredFromSaved.value = true;
  if (s.values && typeof s.values === 'object') for (const [k, v] of Object.entries(s.values)) if (v != null) fill.setValue(k, String(v));
  // 1번 (a)(i) 를 controller_full_name 으로 나누기 전 저장값 — 개인(법인 칸 비어 있음)이면 이름을 옮긴다
  for (let i = 1; i <= repeatCount(fill.values, 'nctrl'); i += 1) {
    const sc = `nctrl_${i}/`;
    if (!st(fill.values[`${sc}controller_full_name`]) && !st(fill.values[`${sc}controller_corp_name`]) && st(fill.values[`${sc}controller_name`])) {
      fill.setValue(`${sc}controller_full_name`, fill.values[`${sc}controller_name`]);
    }
  }
  if (s.signers) signersCtx.importAll(s.signers);
}

async function createTask() {
  if (creating.value) return;
  if (!effectiveTitle.value) { progress.value = 'Please enter a Task Title.'; return; }
  creating.value = true;
  progress.value = editMode.value ? 'Updating task…' : 'Creating task…';
  try {
    const taskTypeCd = st(props.selection.task.TASK_CODE) || TASK_CODE;
    const companyBase = normalizeAventrossAgmForWord(fill.exportPayload());
    const officerData = {
      entityCd: entityCd.value || null, icpCd: entityCd.value || null,
      presetForm: gatherState(), docType: DOC_TYPE,
      isMyPreset: props.selection?.isMyPreset === true, formData: companyBase,
    };
    let taskId: number | null = null;
    if (editMode.value) {
      taskId = Number(props.editTaskId) || null;
      if (!taskId) { progress.value = 'Error: invalid edit task id'; creating.value = false; return; }
      await fetch(`/api/startTask/tasks/${taskId}/data`, { method: 'PUT', credentials: 'include', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ taskData: { formData: officerData } }) });
      await fetch(`/api/startTask/tasks/${taskId}/title`, { method: 'PUT', credentials: 'include', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ taskTitle: effectiveTitle.value }) });
    } else {
      const taskRes = await fetch('/api/startTask/tasks', {
        method: 'POST', credentials: 'include', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          entityCd: entityCd.value || null, taskTypeCd,
          taskTypeName: props.selection.task.TASK_NAME || 'AGM (Audited Companies)',
          taskTitle: effectiveTitle.value,
          taskFlow: (() => {
            const steps = (taskConfigSteps.value || []).map((s) => String(s).toLowerCase());
            return { createDocument: steps.length === 0 ? true : steps.includes('doc'), requestTSign: steps.includes('t-sign'), proceedWithEFiling: steps.includes('e-filing') };
          })(),
          officerData,
        }),
      }).then((r) => r.json());
      taskId = Number(taskRes?.data?.taskId || taskRes?.taskId || 0);
      if (!taskId) { progress.value = `Error creating task: ${taskRes?.error?.message || taskRes?.message || 'no taskId'}`; creating.value = false; return; }
    }

    progress.value = 'Preparing documents…';
    // Edit 재진입 시 selection.documents 가 생성된 문서일 수 있다 → tmpId 기준 1번만
    const seen = new Set<number>();
    const docs = (props.selection.documents || []).filter((d: any) => {
      const tid = Number(d?.tmpId || d?.templateId || 0);
      if (!tid) return true;
      if (seen.has(tid)) return false;
      seen.add(tid); return true;
    });
    const signers = signersCtx.signersOf('agm_Signers');
    const strip = (s: string) => s.split(' — ')[0].trim();
    const docsToSave = docs.map((d: any) => {
      const tid = Number(d?.tmpId || d?.templateId || 0);
      if (!tid) return null;
      const label = strip(String(d?.templateName || d?.docCode || 'AGM').replace(/\.docx?$/i, ''));
      return { templateId: tid, label, personName: st(companyBase.company_name) || 'Company', formData: companyBase, signers: signers.length ? signers : undefined };
    }).filter(Boolean);
    if (docsToSave.length) {
      try {
        const r = await fetch('/api/aiDocuments/incorporation/prepare-documents', { method: 'POST', credentials: 'include', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ taskId, documents: docsToSave, replaceExisting: editMode.value }) }).then((x) => x.json());
        if (!r?.success) progress.value = `⚠ Documents: ${r?.message || 'unknown'}`;
      } catch (e: any) { progress.value = `⚠ Document save failed: ${e?.message || e}`; }
    }
    try { await fetch(`/api/startTask/tasks/${taskId}/document-status`, { method: 'PUT', credentials: 'include', headers: { 'Content-Type': 'application/json' } }); } catch { /* ignore */ }
    try { await fetch('/api/startTask/preset/use-log', { method: 'POST', credentials: 'include', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ presetTaskId: props.selection.task.PRESET_TASK_ID, taskId }) }); } catch { /* ignore */ }
    emit('task-created', { taskId: taskId!, taskTitle: effectiveTitle.value });
    router.push({ name: 'PreSetTaskDetail', query: { taskId: String(taskId) } });
  } catch (e: any) {
    progress.value = e?.message || 'Create failed';
  } finally {
    creating.value = false;
  }
}

defineExpose({ gatherState, restoreState, createTask });
</script>

<template>
  <div class="av-panel" :class="{ studio }">
    <div v-if="!studio" class="co-head">
      <div>
        <div class="co-title">{{ selection.task.TASK_NAME || 'AGM (Audited Companies)' }}</div>
        <div class="co-subtitle">Aventross — AGM Documents (Audited Companies)</div>
      </div>
      <div class="co-actions">
        <v-btn variant="text" size="small" class="text-none" @click="emit('change-task-title')">Change title</v-btn>
        <v-btn variant="text" size="small" class="text-none" @click="emit('change-entity')">Change company</v-btn>
      </div>
    </div>
    <div v-if="!studio" class="co-title-row"><v-text-field v-model="titleModel" density="compact" variant="outlined" hide-details label="Task Title" /></div>
    <div class="co-doc"><AventrossAgmPackageDoc /></div>
    <div v-if="!studio" class="co-foot">
      <v-btn color="primary" class="text-none" :loading="creating" :disabled="!effectiveTitle || creating" @click="createTask">{{ editMode ? 'Update task' : 'Create Task' }}</v-btn>
      <span v-if="progress && !creating" class="co-err">{{ progress }}</span>
    </div>
    <div v-else-if="progress && !creating" class="co-foot"><span class="co-err">{{ progress }}</span></div>
  </div>
</template>

<style scoped>
.av-panel { display: flex; flex-direction: column; gap: 12px; min-height: 0; }
.av-panel.studio .co-foot { position: sticky; bottom: 0; background: #fff; padding: 10px 0; z-index: 2; }
.co-head { display: flex; justify-content: space-between; align-items: flex-start; gap: 12px; }
.co-title { font-size: 16px; font-weight: 800; color: #1a2336; }
.co-subtitle { font-size: 12px; color: #6b7a94; margin-top: 2px; }
.co-actions { display: flex; gap: 4px; flex-shrink: 0; }
.co-title-row { max-width: 520px; }
.co-doc { flex: 1; min-height: 0; }
.co-foot { display: flex; align-items: center; gap: 12px; }
.co-err { font-size: 12px; color: #c0392b; }
</style>
